import React from 'react'
// libs
import { useMutation } from '@apollo/client/react'
// components
import Tree from 'components/Tree'
import Grid from '@mui/material/Grid2'
import ConfirmDeleteModal from 'components/Modals/ConfirmDeleteModal'
// hooks
import { useShowModal } from 'hooks/useShowModal'
// context
import { LocationAPIContext } from 'containers/Locations/layers/API'
// constants
import { Modals } from 'constants/modals'
// graphql
import { LOCATION_DELETE_MUTATION, LOCATIONS_TREE_UPDATE_MUTATION } from 'graphql/locations/mutations'
// themes
import { ActiveLink } from 'themes/views/tree'
import { REACT_SORTABLE_TREE_STYLES } from 'themes'
// utils
import { handleError } from 'utils/errors/handlers'

const Placeholder = ({ handleAddChild }) => {
  return (
    <Grid container justifyContent={'center'} alignItems={'center'} direction={'column'}>
      <Grid>There is no locations here.</Grid>
      <Grid>
        Click <ActiveLink onClick={() => handleAddChild({} as any)}>here</ActiveLink> to create your first location
      </Grid>
    </Grid>
  )
}

const LocationsTree: React.FC = () => {
  const [deleteMutation] = useMutation(LOCATION_DELETE_MUTATION)
  const [treeUpdateMutation] = useMutation(LOCATIONS_TREE_UPDATE_MUTATION)

  const { showModal } = useShowModal({ modalType: Modals.BUSINESS_ENTITY_MODAL })
  const { showModal: showConfirm } = useShowModal({ modalType: Modals.CONFIRM_DELETE_MODAL })

  const { locations, isLoading, refetch } = React.useContext(LocationAPIContext)

  const synchronizeTree = async (tree) => {
    try {
      await treeUpdateMutation({
        variables: {
          locations: tree.map(({ id, parentId, position, expanded }) => ({
            id,
            parentId,
            position,
            expanded
          }))
        }
      })
    } catch (errors) {
      handleError(errors, 'Tree')
    }
  }

  const handleDelete = ({ node }) =>
    showConfirm({
      width: 'sm',
      handleConfirm: async () => {
        try {
          await deleteMutation({
            variables: {
              id: node.id
            }
          })

          await refetch()
        } catch (errors) {
          handleError(errors, 'LocationsTree.handleDelete')
        }
      }
    })

  const handleEdit = ({ node }) =>
    showModal({
      width: 'sm',
      item: node
    })

  const handleAddChild = ({ node }) =>
    showModal({
      width: 'sm',
      parent: node
    })

  const generateActionHandlers = () => ({
    actionsHandlers: {
      handleEditClick: ({ node }) => handleEdit({ node }),
      handleDeleteClick: ({ node }) => handleDelete({ node }),
      handleAddChildClick: ({ node }) => handleAddChild({ node })
    }
  })

  return (
    <>
      {/*//@ts-ignore*/}
      <Tree
        refetch={refetch}
        source={locations}
        isLoading={isLoading}
        synchronizeTree={synchronizeTree}
        generateActionHandlers={generateActionHandlers}
        placeholder={<Placeholder handleAddChild={handleAddChild} />}
        reactVirtualizedListProps={{
          style: REACT_SORTABLE_TREE_STYLES
        }}
      />

      <ConfirmDeleteModal header={'Delete Location'}>Are you sure you want to delete this location?</ConfirmDeleteModal>
    </>
  )
}

export default LocationsTree
